import { ReactNode } from 'react';
import {
  Button,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
} from '@rossum/ui/material';
import {
  OverflowContainerItem,
  RenderInlineOptions,
} from '../overflow-container/useOverflowContainer';

type SelectionPanelActionDescriptor = {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  tooltip?: ReactNode;
  onClick?: () => void;
  // `onCloseParent` is supplied only when the submenu is rendered inside the
  // overflow Menu. Submenus should call it after invoking an action so that the
  // overflow Menu closes before re-renders can unmount its anchor.
  renderSubMenu?: (props: {
    renderTrigger: (state: { isOpen: boolean }) => ReactNode;
    nested?: boolean;
    onCloseParent?: () => void;
  }) => ReactNode;
  'data-cy'?: string;
};

const InlineButton = ({
  action,
  isOpen = false,
}: {
  action: SelectionPanelActionDescriptor;
  isOpen?: boolean;
}) => (
  <Tooltip title={action.tooltip ?? ''}>
    <span>
      <Button
        color="secondary"
        disabled={action.disabled}
        startIcon={action.icon}
        onClick={action.onClick}
        data-cy={action['data-cy']}
        sx={
          isOpen
            ? { backgroundColor: theme => theme.palette.action.hover }
            : undefined
        }
      >
        {action.label}
      </Button>
    </span>
  </Tooltip>
);

const OverflowMenuItem = ({
  action,
  onClick,
  isOpen = false,
}: {
  action: SelectionPanelActionDescriptor;
  onClick?: () => void;
  isOpen?: boolean;
}) => (
  <MenuItem
    disabled={action.disabled}
    onClick={onClick}
    data-cy={action['data-cy'] ? `${action['data-cy']}-overflow` : undefined}
    sx={
      isOpen
        ? { backgroundColor: theme => theme.palette.action.hover }
        : undefined
    }
  >
    {action.icon ? <ListItemIcon>{action.icon}</ListItemIcon> : null}
    <ListItemText>{action.label}</ListItemText>
  </MenuItem>
);

const createSelectionPanelItem = (
  action: SelectionPanelActionDescriptor
): OverflowContainerItem => ({
  id: action.id,

  renderInline: (options?: RenderInlineOptions) => {
    const effectiveAction = options?.measurement
      ? { ...action, 'data-cy': undefined }
      : action;

    if (effectiveAction.renderSubMenu) {
      return effectiveAction.renderSubMenu({
        renderTrigger: ({ isOpen }) => (
          <InlineButton action={effectiveAction} isOpen={isOpen} />
        ),
      });
    }

    return <InlineButton action={effectiveAction} />;
  },

  renderOverflow: ({ close }) => {
    if (action.renderSubMenu) {
      return action.renderSubMenu({
        renderTrigger: ({ isOpen }) => (
          <OverflowMenuItem action={action} isOpen={isOpen} />
        ),
        nested: true,
        onCloseParent: close,
      });
    }

    return (
      <OverflowMenuItem
        action={action}
        onClick={() => {
          action.onClick?.();
          close();
        }}
      />
    );
  },
});

export { createSelectionPanelItem };
export type { SelectionPanelActionDescriptor };
